import memoize from "memoizee";
import { dequal } from "dequal/lite";
import type { UseQueryResult } from "react-query";
import type { AxiosError } from "axios";

import {
  FarmsResponse,
  BasicFarm,
  BasicFarmsDataEnvelope,
} from "../../../models/farms";
import { convertDataToDataById } from "../../../helpers/computations";
import { getAuthToken } from "../../../services/granularAxios";
import { ensureValue } from "../../../services/util";
import { FetcherQueryKey, QueryFunction } from "../../../services/query";
import farmsService from "../../../services/farms";
import {
  numberOfFarmsListsPossiblyActiveAtTheSameTime,
  refinedListOfFarms,
} from "../../../services/helpers/refinements";

export type FarmsFetcherParams = {
  userId: string | undefined;
  operationId: string | undefined;
  catalogFlag: boolean | undefined;
};
export type FarmsQueryKey = FetcherQueryKey<FarmsFetcherParams>;

export const farmsQueryKeyPrefix = "[@fabric/core][farms]";

/**
 * Computes the farmsQueryKey from the given params.
 */
export const computeFarmsQueryKey = (
  params: FarmsFetcherParams,
): FarmsQueryKey => [farmsQueryKeyPrefix, params];

/**
 * Fetches the farms using the farmsService.
 */
export const farmsFetcher: QueryFunction<FarmsResponse, FarmsFetcherParams> =
  async (context) => {
    const userId = ensureValue(context.queryKey[1].userId, "userId");
    const operationId = ensureValue(
      context.queryKey[1].operationId,
      "operationId",
    );
    const catalogFlag = ensureValue(
      context.queryKey[1].catalogFlag,
      "catalogFlag",
    );

    // IMPORTANT: We are not passing the authToken as part of the queryKey
    // because we don't want to invalidate our data on auth token change.
    // The auth token can and will change throughout the lifetime of the app,
    // but that should not invalidate our data.
    const authToken = getAuthToken();

    return farmsService.getFarms(
      {
        authToken,
        userId,
        operationId,
      },
      catalogFlag,
    );
  };

/**
 * Compute the farmsById derived data.
 *
 * This is only for use in computeDataEnvelopeFromResponse.
 * Otherwise, the memoization will not function properly.
 */
export const computeFarmsById = memoize(convertDataToDataById, {
  max: numberOfFarmsListsPossiblyActiveAtTheSameTime,
  profileName: "[enrichedFarms.query.helpers] computeFarmsById",
});

/**
 * Compute the basicFarms data envelope.
 *
 * The memoization of this function may seem useless
 * but it is very important as it keeps the same object reference
 * between renders and across hook instances.
 *
 * This is only for use in computeDataEnvelopeFromResponse.
 * Otherwise, the memoization will not function properly.
 */
export const computeDataEnvelope = memoize(
  (
    farms: BasicFarm[],
    farmsById: Record<string, BasicFarm | undefined>,
  ): BasicFarmsDataEnvelope => {
    return { farms, farmsById };
  },
  {
    max: numberOfFarmsListsPossiblyActiveAtTheSameTime,
    profileName: "[enrichedFarms.query.helpers] computeDataEnvelope",
  },
);

/**
 * Compute the data envelope from the response.
 *
 *
 * This is only for use in computeResultOfUseQuery.
 * Otherwise, the memoization will not function properly.
 *
 * There is no need for this function itself to be memoized
 * because its called functions are memoized and its result is memoized.
 */
export const computeDataEnvelopeFromResponse = (
  farmsResponse: FarmsResponse,
): BasicFarmsDataEnvelope => {
  // Compute the derived data for the data envelope.
  // (Don't worry. These compute functions are globally memoized.)
  const farms = refinedListOfFarms(farmsResponse.farms);
  const farmsById = computeFarmsById(farms);
  const dataEnvelope = computeDataEnvelope(farms, farmsById);
  return dataEnvelope;
};

/**
 * Compute the result of useQuery,
 * converting from the response data to the data envelope.
 *
 * This is only for use in useFarmsQuery.
 * Otherwise, the memoization will not function properly.
 */
export const computeResultOfUseQuery = (
  resultOfUseQuery: UseQueryResult<FarmsResponse, AxiosError>,
): UseQueryResult<BasicFarmsDataEnvelope, AxiosError> => {
  const dataEnvelope = resultOfUseQuery.data
    ? computeDataEnvelopeFromResponse(resultOfUseQuery.data)
    : undefined;

  return {
    ...resultOfUseQuery,
    data: dataEnvelope,
  } as UseQueryResult<BasicFarmsDataEnvelope, AxiosError>;
};

/**
 * Returns whether the current list of farms is equal to the previous.
 *
 * This does a full deep comparison of the list of farms.
 *
 * This is run once per network response as opposed to once per hook instance.
 *
 * This is only meant for use in useFarmsQuery.
 */
export const isDataEqual = (
  prevResponse: FarmsResponse | undefined,
  response: FarmsResponse,
): boolean => {
  if (!prevResponse) {
    return false;
  }

  if (prevResponse.count !== response.count) {
    return false;
  }

  // TODO: Replace with an etag comparison once the endpoint provides it.

  // Deep compare entire list of farms.
  return dequal(prevResponse.farms, response.farms);
};
